import { useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { XMarkIcon, EnvelopeIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { apiClient } from '../services/api'
import { isAxiosError } from 'axios'
import TurnstileWidget, { isTurnstileEnabled } from './TurnstileWidget'

interface EmailModalProps {
  templateIds: number[]
  onClose: () => void
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

function getErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const detail = error.response?.data?.detail
    if (typeof detail === 'string') return detail
    if (error.response?.status === 429) {
      return 'Too many emails sent recently. Please wait and try again.'
    }
    if (error.response?.status === 403) {
      return 'Bot verification failed. Please complete the check and try again.'
    }
    if (error.response?.status === 500) {
      return 'Server error while sending emails. Please try again in a moment.'
    }
  }
  return 'Error sending emails'
}

const DEFAULT_SEND_LIMITS = {
  bulk_max_templates: 10,
  max_per_recipient_hour: 10,
  max_per_recipient_day: 20,
  max_per_ip_hour: 10,
  max_per_ip_day: 20,
} as const

export default function EmailModal({ templateIds, onClose }: EmailModalProps) {
  const [email, setEmail] = useState(() => {
    return localStorage.getItem('showcase_email') || ''
  })
  const [consent, setConsent] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState<'success' | 'error' | 'warning'>('error')
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  // Turnstile tokens are single use, so the widget is remounted after every
  // attempt to hand back a fresh one.
  const [turnstileRound, setTurnstileRound] = useState(0)
  const isSubmittingRef = useRef(false)

  const { data: limits } = useQuery({
    queryKey: ['emailSendLimits'],
    queryFn: () => apiClient.getEmailSendLimits(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const effectiveLimits = limits ?? DEFAULT_SEND_LIMITS

  const trimmedEmail = email.trim()
  const { data: quota, refetch: refetchQuota } = useQuery({
    queryKey: ['emailSendQuota', trimmedEmail],
    queryFn: () => apiClient.getEmailSendQuota(trimmedEmail),
    enabled: isValidEmail(trimmedEmail),
  })

  const maxSendable = quota?.max_sendable_now ?? effectiveLimits.bulk_max_templates

  const idsToSend = useMemo(() => {
    const cap = Math.max(0, maxSendable)
    return templateIds.slice(0, cap)
  }, [templateIds, maxSendable])

  const skippedCount = templateIds.length - idsToSend.length
  const isOverBulkLimit = templateIds.length > effectiveLimits.bulk_max_templates
  const isOverQuota = skippedCount > 0

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (isSubmittingRef.current) {
      return
    }

    if (!trimmedEmail || !consent) {
      setMessageType('error')
      setMessage('Please enter email and confirm consent')
      return
    }

    if (idsToSend.length === 0) {
      setMessageType('error')
      setMessage('Send limit reached for this address. Please try again later.')
      return
    }

    if (isTurnstileEnabled && !turnstileToken) {
      setMessageType('error')
      setMessage('Please complete the verification check below')
      return
    }

    isSubmittingRef.current = true
    setIsLoading(true)
    setMessage('')

    try {
      const result = await apiClient.sendBulkEmails(
        idsToSend,
        trimmedEmail,
        consent,
        turnstileToken
      )

      if (result.success) {
        const allSuccessful = result.results?.every((r: { success: boolean }) => r.success) ?? true

        if (allSuccessful) {
          localStorage.setItem('showcase_email', trimmedEmail)
          document.cookie = `last_email=${trimmedEmail}; path=/; max-age=86400`

          if (skippedCount > 0) {
            setMessageType('warning')
            setMessage(
              `Sent ${idsToSend.length} of ${templateIds.length} selected templates. ` +
                `${skippedCount} were not sent due to send limits — try again later or send in smaller batches.`
            )
            await refetchQuota()
          } else {
            setMessageType('success')
            setMessage('Emails sent successfully!')
            setTimeout(() => {
              onClose()
            }, 2000)
          }
        } else {
          const errors = result.results?.filter((r: { success: boolean }) => !r.success) ?? []
          const errorMessages = errors
            .map((entry: { message?: string }) => entry.message || 'Unknown error')
            .join(', ')
          setMessageType('error')
          setMessage(`Some emails failed to send: ${errorMessages}`)
        }
      } else {
        setMessageType('error')
        setMessage('Error sending: ' + (result.message || 'Unknown error'))
      }
    } catch (error) {
      setMessageType('error')
      setMessage(getErrorMessage(error))
      console.error('Error sending emails:', error)
    } finally {
      setIsLoading(false)
      isSubmittingRef.current = false
      setTurnstileToken(null)
      setTurnstileRound((round) => round + 1)
    }
  }

  const messageStyles = {
    success: 'bg-green-100 text-green-800',
    error: 'bg-red-100 text-red-800',
    warning: 'bg-amber-100 text-amber-900',
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Send Emails</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <p className="text-xs text-gray-500 mb-4 leading-relaxed">
            Up to {effectiveLimits.bulk_max_templates} templates per send,{' '}
            {effectiveLimits.max_per_recipient_hour}/hour and{' '}
            {effectiveLimits.max_per_recipient_day}/day per email address.
          </p>

        {(isOverBulkLimit || isOverQuota) && idsToSend.length > 0 && (
          <div className="mb-4 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
            <div>
              {isOverBulkLimit && (
                <p>
                  You selected {templateIds.length} templates, but only{' '}
                  <strong>{effectiveLimits.bulk_max_templates}</strong> can be sent at once.
                </p>
              )}
              {isOverQuota && quota && (
                <p className={isOverBulkLimit ? 'mt-1' : ''}>
                  {quota.remaining_recipient_hour} remaining this hour for this address
                  ({quota.sent_recipient_hour}/{quota.max_per_recipient_hour} used).
                  We will send the first <strong>{idsToSend.length}</strong>.
                </p>
              )}
            </div>
          </div>
        )}

        {quota && idsToSend.length === 0 && isValidEmail(trimmedEmail) && (
          <div className="mb-4 flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
            <p>
              Send limit reached for this address ({quota.sent_recipient_hour}/{quota.max_per_recipient_hour} this hour).
              Please try again later.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="your@email.com"
              required
            />
          </div>

          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="consent"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              required
            />
            <label htmlFor="consent" className="text-sm text-gray-700">
              I consent to receive emails at the specified email address
            </label>
          </div>

          <TurnstileWidget key={turnstileRound} onToken={setTurnstileToken} />

          {message && (
            <div className={`p-3 rounded-lg text-sm ${messageStyles[messageType]}`}>
              {message}
            </div>
          )}

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || idsToSend.length === 0}
              className="flex-1 flex items-center justify-center space-x-2 bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <EnvelopeIcon className="w-4 h-4" />
                  <span>
                    Send ({idsToSend.length}
                    {skippedCount > 0 ? ` of ${templateIds.length}` : ''})
                  </span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
